import React, { createContext, useState, useEffect, useContext } from 'react';
import api from '../api/axios';
import { useLanguage } from './LanguageContext';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const { t } = useLanguage();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [impersonating, setImpersonating] = useState(false);

    useEffect(() => {
        // Handle Google Sign-In redirect callback: any page may receive #google_token=<jwt>
        // This happens when GSI ux_mode='redirect' is used (PWA / mobile)
        const hash = window.location.hash;
        if (hash.includes('google_token=')) {
            const tokenMatch = hash.match(/google_token=([^&]+)/);
            if (tokenMatch) {
                const token = decodeURIComponent(tokenMatch[1]);
                window.history.replaceState(null, '', window.location.pathname + window.location.search);
                try {
                    const payloadB64 = token.split('.')[1];
                    const payload = JSON.parse(atob(payloadB64.replace(/-/g, '+').replace(/_/g, '/')));
                    const userData = { id: payload.id, email: payload.email, role: payload.role, orgId: payload.orgId };
                    localStorage.setItem('token', token);
                    localStorage.setItem('user', JSON.stringify(userData));
                    setUser(userData);
                    setLoading(false);
                    // Navigate to the correct page now that we have a user
                    const dest = payload.role === 'user' ? '/webmail' : '/dashboard';
                    window.location.replace(dest);
                    return; // skip the regular localStorage read below
                } catch (e) {
                    console.error('[Auth] Failed to parse google_token hash:', e.message);
                }
            }
        }

        const token = localStorage.getItem('token');
        const userData = localStorage.getItem('user');
        const isImpersonating = localStorage.getItem('impersonating') === 'true';
        
        if (token && userData) {
            setUser(JSON.parse(userData));
            setImpersonating(isImpersonating);
        }
        setLoading(false);
    }, []);

    const login = async (email, password, twoFactorCode, rememberMe = false, rememberMode = null) => {
        try {
            // rememberMode supersedes rememberMe ('forever' | 'extended' | null/falsy)
            // Keep rememberMe for backwards compatibility with older clients.
            const response = await api.post('/auth/login', {
                email, password, twoFactorCode,
                rememberMe: rememberMode === 'forever' || rememberMode === 'extended' || !!rememberMe,
                rememberMode: rememberMode || (rememberMe ? 'extended' : null),
            });

            // Check if 2FA is required
            if (response.data.requires2FA) {
                return { success: false, requires2FA: true, userId: response.data.userId };
            }

            const { token, user, refreshToken, rememberMode: serverMode } = response.data;

            localStorage.setItem('token', token);
            localStorage.setItem('user', JSON.stringify(user));
            if (refreshToken) {
                localStorage.setItem('refreshToken', refreshToken);
            } else {
                localStorage.removeItem('refreshToken');
            }
            if (serverMode) {
                localStorage.setItem('rememberMode', serverMode);
            } else {
                localStorage.removeItem('rememberMode');
            }
            localStorage.removeItem('impersonating');
            localStorage.removeItem('adminToken');
            localStorage.removeItem('adminUser');
            setUser(user);
            setImpersonating(false);
            return { success: true, user };
        } catch (error) {
            return { 
                success: false, 
                message: error.response?.data?.message || t('common.errorLogin')
            };
        }
    };

    const logout = () => {
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
            // Invalidate server-side refresh token (best-effort, non-blocking)
            api.post('/auth/logout', { refreshToken }).catch(() => {});
        }
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('rememberMode');
        localStorage.removeItem('impersonating');
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        setUser(null);
        setImpersonating(false);
    };

    // Impersonate a user (admin views as another user)
    const impersonateUser = (targetUser, targetToken) => {
        // Save admin credentials
        localStorage.setItem('adminToken', localStorage.getItem('token'));
        localStorage.setItem('adminUser', localStorage.getItem('user'));
        
        // Switch to target user
        localStorage.setItem('token', targetToken);
        localStorage.setItem('user', JSON.stringify(targetUser));
        localStorage.setItem('impersonating', 'true');
        
        setUser(targetUser);
        setImpersonating(true);
    };

    // Return to admin from impersonation
    const stopImpersonating = () => {
        const adminToken = localStorage.getItem('adminToken');
        const adminUser = localStorage.getItem('adminUser');
        
        if (adminToken && adminUser) {
            localStorage.setItem('token', adminToken);
            localStorage.setItem('user', adminUser);
            localStorage.removeItem('adminToken');
            localStorage.removeItem('adminUser');
            localStorage.removeItem('impersonating');
            
            setUser(JSON.parse(adminUser));
            setImpersonating(false);
        }
    };

    return (
        <AuthContext.Provider value={{ user, setUser, login, logout, loading, impersonating, impersonateUser, stopImpersonating }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
